import React from 'react';
import { Box, Button, Theme, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useDropzone } from 'react-dropzone';
import { CloudUpload, GetApp, GridOn } from '@material-ui/icons';
import { BUTTON_ICON_STYLE, getToolbarButtonStyle } from '../import-button-styles';
import templateUrl from '../../../../../assets/activity-import-template.csv';

const useStyles = makeStyles((theme: Theme) => ({
  page: {
    minHeight: '100%',
    backgroundColor: '#ffffff',
    padding: '12px 16px 32px',
    boxSizing: 'border-box',
  },
  strip: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    '& > * + *': {
      marginLeft: 10,
    },
  },
  title: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#333333',
    marginRight: 'auto',
  },
  container: {
    maxWidth: 820,
    margin: '24px auto 0',
    '& > * + *': {
      marginTop: 20,
    },
  },
  intro: {
    fontSize: '13px',
    color: theme.color.grey.neutral300,
  },
  uploadContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '36px 24px',
    backgroundColor: '#ffffff',
    border: '2px dashed #D9D9D9',
    borderRadius: 6,
    cursor: 'pointer',
    '&:hover, &:focus-visible': {
      borderColor: theme.color.secondary.main900,
      outline: 'none',
    },
  },
  uploadIcon: {
    fontSize: 40,
    marginRight: 16,
    color: theme.color.secondary.main900,
  },
  uploadTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#333333',
  },
  uploadSubtitle: {
    fontSize: '13px',
    color: theme.color.grey.neutral300,
  },
  button: getToolbarButtonStyle(theme),
  reference: {
    backgroundColor: '#ffffff',
    border: `1px solid ${theme.color.grey.border}`,
    borderRadius: 6,
    padding: '14px 18px',
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: theme.color.grey.neutral500,
    marginBottom: 6,
  },
  referenceRow: {
    display: 'flex',
    padding: '8px 0',
    borderBottom: `1px solid ${theme.color.grey.border}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  referenceKey: {
    width: 120,
    flexShrink: 0,
    marginRight: 16,
    fontWeight: 600,
    fontSize: '13px',
    color: '#333333',
  },
  referenceValue: {
    fontSize: '13px',
    color: theme.color.grey.neutral300,
  },
  error: {
    color: theme.palette.error.main,
    fontSize: '13px',
  },
}));

const TYPE_REFERENCE = [
  [
    'regular',
    'Requires start_date and frequency. Optional end_date, repeat_days (weekly/fortnightly) and repeat_interval.',
  ],
  ['once-off', 'Requires start_date.'],
  ['flexible', 'Optional dates. A roster is created for each date.'],
  ['event', 'Requires dates. A roster is created for each date, sessions repeat across every date.'],
  ['self-directed', 'No dates. Sessions are the actions volunteers complete.'],
];

const FORMAT_REFERENCE = [
  ['Sessions', 'session_1, session_1_desc, session_1_start ... add session_4, session_5 etc. for more.'],
  [
    'One-off sessions',
    'session_oneoff_1_date, session_oneoff_1, session_oneoff_1_start ... Regular and event only. Dates must be activity dates.',
  ],
  ['Yes / no', 'yes or no. Leave blank to use the default for the activity type.'],
  ['Lists', 'Separate tags, roles, dates and repeat days with ;'],
  ['Dates', 'YYYY-MM-DD or DD/MM/YYYY'],
  ['Times', 'HH:mm, e.g. 09:00'],
  ['Report type', 'Report type name. Blank for no report, "auto" for no report (impact tracked).'],
];

interface Props {
  onUpload: (file: File) => void;
  onStartBlank: () => void;
  parseError?: string;
}

export function UploadActivitiesStep({ onUpload, onStartBlank, parseError }: Props) {
  const classes = useStyles();
  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    accept: '.csv,text/csv',
    onDrop: (files: File[]) => {
      if (files[0]) onUpload(files[0]);
    },
  });

  return (
    <Box className={classes.page}>
      <Box className={classes.strip}>
        <Typography className={classes.title}>Import Activities</Typography>
        <Button
          className={classes.button}
          startIcon={<GetApp style={BUTTON_ICON_STYLE} />}
          href={templateUrl}
          download='Activity Import Template.csv'
        >
          Download template
        </Button>
        <Button className={classes.button} startIcon={<GridOn style={BUTTON_ICON_STYLE} />} onClick={onStartBlank}>
          Start with a blank grid
        </Button>
      </Box>

      <Box className={classes.container}>
        <Typography className={classes.intro}>
          Upload a CSV with one activity per row. You can review and edit everything before any activities are created.
        </Typography>

        <div {...getRootProps()} className={classes.uploadContainer}>
          <input {...getInputProps()} />
          <CloudUpload className={classes.uploadIcon} />
          <Box>
            <Typography className={classes.uploadTitle}>Upload CSV file</Typography>
            <Typography className={classes.uploadSubtitle}>Click or drag a file here</Typography>
          </Box>
        </div>
        {parseError && <Typography className={classes.error}>{parseError}</Typography>}

        <Box className={classes.reference}>
          <Typography className={classes.sectionTitle}>Activity types</Typography>
          {TYPE_REFERENCE.map(([key, value]) => (
            <Box key={key} className={classes.referenceRow}>
              <Typography className={classes.referenceKey}>{key}</Typography>
              <Typography className={classes.referenceValue}>{value}</Typography>
            </Box>
          ))}
        </Box>

        <Box className={classes.reference}>
          <Typography className={classes.sectionTitle}>Formats</Typography>
          {FORMAT_REFERENCE.map(([key, value]) => (
            <Box key={key} className={classes.referenceRow}>
              <Typography className={classes.referenceKey}>{key}</Typography>
              <Typography className={classes.referenceValue}>{value}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
